import type {
  GroupLifeFrontPayment,
  GroupLifeQuoteDetails,
  GroupLifeTarifacaoLife,
  GroupLifeTarifacaoParties,
} from '@/services/group-life';
import type {
  InstallmentItem,
  PolicyDetailsResponse,
} from '@/services/policies/types';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { coverageTotalSumInsured } from '@/features/analyses/pme/coverage-rates';
import { resolveAverageRatePerMille } from '@/features/quotes/utils/group-life/average-rate.util';
import { isServiceCoverage } from '@/features/quotes/utils/group-life/service-coverage.util';
import { PME_BILLING_FREQUENCY_OPTIONS } from '@/features/proposals/components/pme/proposal.mock';
import { getInsuranceTypeLabel } from '../policy-section.shared';

/**
 * View-model da página de detalhe da apólice PME (930016).
 *
 * Função pura, sem I/O: recebe as três fontes já resolvidas pelos hooks e
 * devolve exatamente as strings que a tela renderiza. Mesmo desenho do
 * `map-policy-details-to-capital-global-view.util.ts`, mas com fontes
 * diferentes — a `GroupLifePolicyFactory` do BFF não preenche `customers`,
 * `agents`, `lobs` nem `paymentInfo` para o 930016, então corretor e dia de
 * vencimento precisam vir da tarifação e das parcelas.
 */

// ─── View model ──────────────────────────────────────────────────────────────

export type PmePremiumCardView = {
  totalValue: string;
  liquidValue: string;
  iofValue: string;
  /** Sem origem em nenhum DTO hoje — sempre o placeholder. */
  index: string;
  paymentFrequency: string;
  product: string;
  quoteNumber: string;
  dueDay: string;
  status: string;
};

export type PmePolicyEmission = {
  effectiveDate: string;
  expiryDate: string;
  issueDate: string;
  insuranceType: string;
};

export type PmePolicyStipulantHeader = {
  document: string;
  socialName: string;
  email: string;
  phone: string;
};

export type PmePolicyBasicData = {
  document: string;
  socialName: string;
  mainActivity: string;
  email: string;
  phone: string;
};

/** Estipulante master ou um subestipulante, com o bucket de vidas dele. */
export type PmePolicyEntity = {
  /** `__STIPULATOR__` para o master; o id do sub para os demais. */
  targetId: string;
  label: string;
  basicData: PmePolicyBasicData;
};

export type PmePolicyCoverageRow = {
  name: string;
  /** Detalhe sob o nome — nas diárias, "30 diárias × R$ 100,00". */
  detail?: string;
  totalInsuredCapital: string;
  averageRate: string;
  monthlyPremium: string;
};

export type PmePolicyServiceRow = {
  name: string;
  valuePerInsured: string;
  monthlyValue: string;
};

export type PmePolicyDemonstrativeRow = {
  description: string;
  value: string;
  iof: string;
  total: string;
};

export type PmeCoveragesServicesView = {
  coverages: PmePolicyCoverageRow[];
  averageMonthlyRate: string;
  services: PmePolicyServiceRow[];
  demonstrative: PmePolicyDemonstrativeRow[];
};

export type PmePolicyCoBroker = {
  name: string;
  susepCode: string;
  document: string;
  sharePercent: string;
};

export type PmePolicyCommercialization = {
  broker: string;
  /** Participação do corretor principal — só existe havendo co-corretagem. */
  leaderSharePercent: string | null;
  brokeragePercent: string;
  agencingPercent: string;
  surchargePercent: string;
  coBrokers: PmePolicyCoBroker[];
};

export type PmePolicyView = {
  policyNumber: string;
  premium: PmePremiumCardView;
  emission: PmePolicyEmission;
  stipulantHeader: PmePolicyStipulantHeader;
  entities: PmePolicyEntity[];
  coveragesServices: PmeCoveragesServicesView;
  commercialization: PmePolicyCommercialization;
};

export type MapPmePolicyInput = {
  policy: PolicyDetailsResponse;
  proposal?: GroupLifeQuoteDetails;
  /** Vidas tarifadas da apólice inteira — insumo das tabelas de coberturas. */
  pricingLives?: GroupLifeTarifacaoLife[];
  /** Total de vidas da apólice; ausente → quantidade de `pricingLives`. */
  livesCount?: number;
  parties?: GroupLifeTarifacaoParties;
  installments?: InstallmentItem[];
  /** Código da cobertura → nome amigável, vindo do coverage schema. */
  coverageNameByCode?: Map<string, string>;
  /** Códigos E nomes que o schema classifica como serviço/assistência. */
  serviceCoverageKeys?: Set<string>;
  /** Detalhe das diárias por código/nome (ver `dailyAllowanceDetails`). */
  dailyAllowanceByCoverage?: Map<string, string>;
  productName: string;
  statusLabel: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const PME_MASTER_STIPULATOR_TARGET = '__STIPULATOR__';

const PLACEHOLDER = '—';
const MONTHS_IN_YEAR = 12;

function money(value?: number | null): string {
  if (value == null) return PLACEHOLDER;
  return formatCurrency(value);
}

function orPlaceholder(value?: string | null): string {
  const trimmed = (value ?? '').trim();
  return trimmed || PLACEHOLDER;
}

/** Percentuais do snapshot vêm mascarados em pt-BR ("45,00") — falta o "%". */
function percent(value?: string | number | null): string {
  const raw = String(value ?? '').trim();
  if (!raw) return PLACEHOLDER;
  return raw.endsWith('%') ? raw : `${raw}%`;
}

/**
 * Dia de vencimento a partir da primeira parcela. Para o PME o
 * `policy.paymentInfo` chega vazio (a factory do BFF não o preenche), então as
 * fontes são as parcelas do i4Pro e, quando elas ainda não existem (apólice
 * recém-emitida, cobrança não gerada), o dia escolhido no step de pagamento da
 * proposta (`payment.dueDay`, lido do `InstallmentList` do snapshot).
 */
function resolveDueDay(
  installments?: InstallmentItem[],
  paymentDueDay?: string
): string {
  const dueDate = installments?.[0]?.dueDate;
  if (!dueDate) {
    const day = Number((paymentDueDay ?? '').trim());
    return Number.isInteger(day) && day >= 1 && day <= 31
      ? String(day)
      : PLACEHOLDER;
  }
  // Formato ISO (yyyy-MM-dd...): extrai o dia sem depender de timezone.
  const iso = String(dueDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return String(Number(iso[3]));
  // Formato pt-BR (dd/MM/yyyy).
  const br = String(dueDate).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) return String(Number(br[1]));
  const day = new Date(dueDate).getDate();
  return Number.isNaN(day) ? PLACEHOLDER : String(day);
}

/**
 * Periodicidade de pagamento = a FREQUÊNCIA DE COBRANÇA escolhida no step de
 * pagamento da proposta. A fonte anterior era `calculation.frequency`, que o
 * BFF deriva do `InstallPeriod` do snapshot — no PME ele chega 1 ("Anual") e a
 * apólice exibia "Anual" ao lado de um prêmio mensal. O resto do produto
 * (resumo da cotação, análise, PDFs) já usa a frequência de cobrança.
 */
function resolveBillingFrequency(payment?: GroupLifeFrontPayment): string {
  const value = (payment?.billingFrequency ?? '').trim().toLowerCase();
  const option = PME_BILLING_FREQUENCY_OPTIONS.find(o => o.value === value);
  return option?.label ?? 'Mensal';
}

// ─── Section mappers ─────────────────────────────────────────────────────────

function mapPremiumCard(input: MapPmePolicyInput): PmePremiumCardView {
  const calculation = input.proposal?.calculation;

  // Mesma mensalização da análise PME: o card do mockup é MENSAL, mas o
  // `calculation` do InsureMO é anual quando não há `monthlyPremium`.
  // `monthlyPremium` ZERADO conta como ausente: as 12 parcelas do PME vêm do
  // InsureMO só com as datas (`InstallmentAmount` 0), e um `??` deixava o card
  // em R$ 0,00 com o prêmio anual preenchido logo ao lado.
  const monthlyTotal =
    (calculation?.monthlyPremium ?? 0) > 0
      ? (calculation?.monthlyPremium as number)
      : (calculation?.totalPremium ?? 0) / MONTHS_IN_YEAR;
  const monthlyIof = calculation?.iof ? calculation.iof / MONTHS_IN_YEAR : 0;
  const monthlyNet = Math.max(monthlyTotal - monthlyIof, 0);

  return {
    totalValue: money(monthlyTotal),
    liquidValue: money(monthlyNet),
    iofValue: money(monthlyIof),
    index: PLACEHOLDER,
    paymentFrequency: resolveBillingFrequency(input.proposal?.payment),
    product: orPlaceholder(input.productName),
    quoteNumber: orPlaceholder(input.proposal?.proposalNumber),
    dueDay: resolveDueDay(input.installments, input.proposal?.payment?.dueDay),
    status: orPlaceholder(input.statusLabel),
  };
}

function mapEmission(policy: PolicyDetailsResponse): PmePolicyEmission {
  return {
    effectiveDate: orPlaceholder(formatDateToBrazil(policy.effectiveDate)),
    expiryDate: orPlaceholder(formatDateToBrazil(policy.expiryDate)),
    issueDate: orPlaceholder(
      formatDateToBrazil(
        policy.externalEffectiveDate || policy.issueDate || policy.effectiveDate
      )
    ),
    insuranceType: getInsuranceTypeLabel(policy.typeOfInsurance),
  };
}

function mapStipulantHeader(
  proposal?: GroupLifeQuoteDetails
): PmePolicyStipulantHeader {
  const stipulator = proposal?.stipulator;
  return {
    document: orPlaceholder(
      formatRegisterNumber(stipulator?.registerNumber ?? '')
    ),
    socialName: orPlaceholder(
      stipulator?.tradeName || stipulator?.responsibleName
    ),
    email: orPlaceholder(stipulator?.email),
    phone: orPlaceholder(stipulator?.phone),
  };
}

function mapEntities(input: MapPmePolicyInput): PmePolicyEntity[] {
  const proposal = input.proposal;
  const stipulator = proposal?.stipulator;
  const entities: PmePolicyEntity[] = [];

  if (stipulator) {
    entities.push({
      targetId: PME_MASTER_STIPULATOR_TARGET,
      label: 'Estipulante',
      basicData: {
        document: orPlaceholder(
          formatRegisterNumber(stipulator.registerNumber ?? '')
        ),
        socialName: orPlaceholder(
          stipulator.tradeName || stipulator.responsibleName
        ),
        // O BFF já devolve "código — descrição" no `parties` da tarifação; o
        // `cnaeCode` do snapshot é só o código cru.
        mainActivity: orPlaceholder(
          input.parties?.proponente?.mainActivity || stipulator.cnaeCode
        ),
        email: orPlaceholder(stipulator.email),
        phone: orPlaceholder(stipulator.phone),
      },
    });
  }

  (proposal?.subStipulators ?? []).forEach((sub, index) => {
    entities.push({
      targetId: sub.id,
      label: `Subestipulante ${index + 1}`,
      basicData: {
        document: orPlaceholder(formatRegisterNumber(sub.cnpj ?? '')),
        socialName: orPlaceholder(sub.legalName),
        // Não há lookup de CNAE por subestipulante — fica o código.
        mainActivity: orPlaceholder(sub.cnaeCode),
        email: orPlaceholder(sub.email),
        phone: orPlaceholder(sub.phone),
      },
    });
  });

  return entities;
}

/**
 * As três tabelas de "Coberturas e Serviços". Reproduz a derivação já validada
 * na análise PME: o demonstrativo do cálculo é particionado em coberturas x
 * serviços, o capital vem das vidas tarifadas e a taxa é derivada da própria
 * linha (prêmio ÷ capital × 1000), como na cotação e na proposta.
 */
function mapCoveragesServices(
  input: MapPmePolicyInput
): PmeCoveragesServicesView {
  const breakdown = input.proposal?.calculation?.breakdown ?? [];
  const lives = input.pricingLives ?? [];
  const livesCount = input.livesCount ?? lives.length;
  const coverageNameByCode =
    input.coverageNameByCode ?? new Map<string, string>();
  const serviceCoverageKeys = input.serviceCoverageKeys ?? new Set<string>();

  const sumInsuredByCoverage = coverageTotalSumInsured(lives);

  const coverages: PmePolicyCoverageRow[] = [];
  const services: PmePolicyServiceRow[] = [];
  // Totais das linhas com capital, para a TAXA MÉDIA MENSAL do rodapé.
  let coveredMonthlyPremium = 0;
  let coveredCapital = 0;

  for (const item of breakdown) {
    const name = coverageNameByCode.get(item.description) ?? item.description;

    if (
      serviceCoverageKeys.has(item.description) ||
      serviceCoverageKeys.has(name) ||
      isServiceCoverage(item.description, name)
    ) {
      // "Valor Mensal": o total do demonstrativo ÷ 12; "Valor Por Segurado":
      // esse mensal ÷ vidas da apólice.
      const monthlyValue = item.total / MONTHS_IN_YEAR;
      services.push({
        name,
        valuePerInsured:
          livesCount > 0 ? money(monthlyValue / livesCount) : PLACEHOLDER,
        monthlyValue: money(monthlyValue),
      });
      continue;
    }

    const totalSumInsured =
      sumInsuredByCoverage.get(item.description) ??
      sumInsuredByCoverage.get(name);
    const monthlyPremium = item.total / MONTHS_IN_YEAR;
    if (totalSumInsured != null) {
      coveredMonthlyPremium += monthlyPremium;
      coveredCapital += totalSumInsured;
    }

    const detail =
      input.dailyAllowanceByCoverage?.get(item.description) ??
      input.dailyAllowanceByCoverage?.get(name);
    coverages.push({
      name,
      ...(detail ? { detail } : {}),
      totalInsuredCapital:
        totalSumInsured == null ? PLACEHOLDER : money(totalSumInsured),
      averageRate: resolveAverageRatePerMille(monthlyPremium, totalSumInsured),
      monthlyPremium: money(monthlyPremium),
    });
  }

  // Demonstrativo: valores ANUAIS, direto do breakdown — diferente das duas
  // tabelas acima, que o mockup mostra mensalizadas.
  const demonstrative: PmePolicyDemonstrativeRow[] = breakdown.map(item => ({
    description: coverageNameByCode.get(item.description) ?? item.description,
    value: money(item.value),
    iof: money(item.iof),
    total: money(item.total),
  }));

  return {
    coverages,
    // Mesma fórmula da coluna, aplicada aos totais.
    averageMonthlyRate: resolveAverageRatePerMille(
      coveredCapital === 0 ? null : coveredMonthlyPremium,
      coveredCapital
    ),
    services,
    demonstrative,
  };
}

function mapCommercialization(
  input: MapPmePolicyInput
): PmePolicyCommercialization {
  const commercialization = input.proposal?.commercialization;
  const coBrokers = (commercialization?.coBrokers ?? []).map(coBroker => ({
    name: orPlaceholder(coBroker.name),
    susepCode: orPlaceholder(coBroker.susepCode),
    // `formatRegisterNumber('')` devolve "NA": sem CNPJ, fica o placeholder.
    document: coBroker.registerNumber?.trim()
      ? formatRegisterNumber(coBroker.registerNumber)
      : PLACEHOLDER,
    sharePercent: percent(coBroker.sharePercent),
  }));

  return {
    // `policy.agents` chega vazio para o 930016 — o corretor só existe no
    // cabeçalho da tarifação.
    broker: orPlaceholder(input.parties?.broker?.name),
    // Mesma regra da carta comercial: sem a participação gravada, o líder fica
    // com 100% (`build-commercial-letter-payload`).
    leaderSharePercent: coBrokers.length
      ? percent(commercialization?.leaderSharePercent || '100,00')
      : null,
    brokeragePercent: percent(commercialization?.brokeragePercent),
    agencingPercent: percent(
      commercialization?.agencingPercent || commercialization?.agencyPercent
    ),
    surchargePercent: percent(commercialization?.surchargePercent),
    coBrokers,
  };
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function mapPolicyToPmeView(input: MapPmePolicyInput): PmePolicyView {
  return {
    policyNumber: orPlaceholder(
      input.policy.externalPolicyNo || input.policy.policyNo
    ),
    premium: mapPremiumCard(input),
    emission: mapEmission(input.policy),
    stipulantHeader: mapStipulantHeader(input.proposal),
    entities: mapEntities(input),
    coveragesServices: mapCoveragesServices(input),
    commercialization: mapCommercialization(input),
  };
}
